/**
 * Active Processes Registry
 * 
 * Tracks active long-running processes to prevent logout during critical operations.
 * Used by AuthContext to check if logout should be prevented.
 */

class ActiveProcessesRegistry {
  constructor() {
    this.processes = new Map(); // Map<processId, { type, startTime, metadata }>
    this.loadFromStorage();
  }
  
  /**
   * Load processes from localStorage on initialization
   */
  loadFromStorage() {
    try {
      const stored = localStorage.getItem('activeProcessesRegistry');
      if (stored) {
        const data = JSON.parse(stored);
        // Only restore processes that are less than 1 hour old
        const oneHourAgo = Date.now() - (60 * 60 * 1000);
        for (const [processId, processData] of Object.entries(data)) {
          if (processData.startTime > oneHourAgo) {
            this.processes.set(processId, processData);
          }
        }
        console.log(`[ActiveProcessesRegistry] Loaded ${this.processes.size} processes from storage`);
      }
    } catch (e) {
      console.warn('[ActiveProcessesRegistry] Error loading from storage:', e);
    }
  }
  
  /**
   * Save processes to localStorage
   */
  saveToStorage() {
    try {
      const data = {};
      for (const [processId, processData] of this.processes.entries()) {
        data[processId] = processData;
      }
      localStorage.setItem('activeProcessesRegistry', JSON.stringify(data));
    } catch (e) {
      console.warn('[ActiveProcessesRegistry] Error saving to storage:', e);
    }
  }

  /**
   * Register an active process
   * @param {string} processId - Unique process identifier
   * @param {string} type - Process type (e.g., 'file_upload', 'ingestion', 'job')
   * @param {Object} metadata - Additional metadata about the process
   */
  register(processId, type, metadata = {}) {
    this.processes.set(processId, {
      type,
      startTime: Date.now(),
      metadata,
      ...metadata
    });
    this.saveToStorage(); // Persist to localStorage
    console.log(`[ActiveProcessesRegistry] Registered process: ${processId} (${type})`);
  }

  /**
   * Unregister a process
   * @param {string} processId - Process identifier to remove
   */
  unregister(processId) {
    const existed = this.processes.delete(processId);
    if (existed) {
      this.saveToStorage(); // Persist to localStorage
      console.log(`[ActiveProcessesRegistry] Unregistered process: ${processId}`);
    }
    return existed;
  }

  /**
   * Check if there are any active processes
   * @returns {boolean}
   */
  hasActiveProcesses() {
    return this.processes.size > 0;
  }

  /**
   * Get all active processes
   * @returns {Array} Array of { processId, type, startTime, metadata }
   */
  getActiveProcesses() {
    return Array.from(this.processes.entries()).map(([processId, data]) => ({
      processId,
      ...data
    }));
  }

  /**
   * Get active processes by type
   * @param {string} type - Process type to filter
   * @returns {Array}
   */
  getActiveProcessesByType(type) {
    return this.getActiveProcesses().filter(p => p.type === type);
  }

  /**
   * Clear all processes (use with caution)
   */
  clear() {
    const count = this.processes.size;
    this.processes.clear();
    try {
      localStorage.removeItem('activeProcessesRegistry');
    } catch (e) {
      console.warn('[ActiveProcessesRegistry] Error clearing storage:', e);
    }
    console.log(`[ActiveProcessesRegistry] Cleared ${count} processes`);
  }

  /**
   * Clean up old processes (older than maxAge milliseconds)
   * @param {number} maxAge - Maximum age in milliseconds (default: 1 hour)
   */
  cleanup(maxAge = 60 * 60 * 1000) {
    const now = Date.now();
    let cleaned = 0;
    
    for (const [processId, data] of this.processes.entries()) {
      if (now - data.startTime > maxAge) {
        this.processes.delete(processId);
        cleaned++;
      }
    }
    
    if (cleaned > 0) {
      this.saveToStorage(); // Persist cleanup to localStorage
      console.log(`[ActiveProcessesRegistry] Cleaned up ${cleaned} old processes`);
    }
  }
}

// Singleton instance
const registry = new ActiveProcessesRegistry();

// Auto-cleanup every 30 minutes
setInterval(() => {
  registry.cleanup();
}, 30 * 60 * 1000);

export default registry;

